import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import monitor, {
  isMobile,
  isTablet,
  isIos,
  AlertData,
  DeviceOrientation,
  EnabledVitalSigns,
  FaceSessionOptions,
  HealthMonitorCodes,
  HealthMonitorSession,
  ImageValidity,
  OfflineMeasurements,
  SessionState,
  VitalSigns,
  VitalSignsResults,
  StressLevel,
  WellnessLevel,
  SnsZone,
  PnsZone,
  HighHemoglobinA1CRisk,
  HighBloodPressureRisk,
  HighFastingGlucoseRisk,
  HighTotalCholesterolRisk,
  LowHemoglobinRisk,
  ConfidenceLevel,
  AscvdRiskLevel,
  
  SubjectDemographic,
  Sex,
  SmokingStatus,
} from '@biosensesignal/web-sdk';
import { InfoType, InfoData } from '../types';

const useMonitor = (
  video: MutableRefObject<HTMLVideoElement>,
  cameraId: string,
  processingTime: number,
  licenseKey: string,
  productId: string,
  startMeasuring: boolean,
) => {
  const [session, setSession] = useState<HealthMonitorSession>();
  const [sessionState, setSessionState] = useState<SessionState>();
  const [isMonitorReady, setIsMonitorReady] = useState<boolean>();
  const [enabledVitalSigns, setEnabledVitalSigns] = useState<EnabledVitalSigns>();
  const [offlineMeasurements, setOfflineMeasurements] = useState<OfflineMeasurements>();
  const [vitalSigns, setVitalSigns] = useState<VitalSigns | null>();

  const [error, setError] = useState<AlertData>({ code: -1 });
  const [warning, setWarning] = useState<AlertData>({ code: -1 });
  const [info, setInfo] = useState<InfoData>({ type: InfoType.NONE });
  const isDismissing = useRef<boolean>(false);

  const setInfoWithDismiss = useCallback(
    (info: InfoData, seconds?: number) => {
      if (!isDismissing.current) {
        setInfo(info);
        if (seconds) {
          isDismissing.current = true;
          setTimeout(() => {
            setInfo({ type: InfoType.NONE });
            isDismissing.current = false;
          }, seconds * 1000);
        }
      }
    },
    [InfoType, setInfo, info, isDismissing, isDismissing.current],
  );

  const updateVitalSigns = useCallback((vitalSigns) => {
    setVitalSigns((prev) => ({
      ...prev,
      ...vitalSigns,
    }));
  }, []);

  const onVitalSign = useCallback((vitalSign: VitalSigns) => {
    updateVitalSigns(vitalSign);
  }, []);

  const onFinalResults = useCallback((vitalSignsResults: VitalSignsResults) => {
    setVitalSigns(null);
    updateVitalSigns(vitalSignsResults.results);
  }, []);

  const onError = (errorData: AlertData) => {
    setError(errorData);
  };

  const onWarning = (warningData: AlertData) => {
    if (
      warningData.code ===
      HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
    ) {
      setVitalSigns(null);
    }
    setWarning(warningData);
  };

  const onStateChange = useCallback((state: SessionState) => {
    setSessionState(state);
    if (state === SessionState.MEASURING) {
      setVitalSigns(null);
    }
  }, []);

  const onEnabledVitalSigns = useCallback((vitalSigns: EnabledVitalSigns) => {
    setEnabledVitalSigns(vitalSigns);
  }, []);

  const onOfflineMeasurement = useCallback(
    (offlineMeasurements: OfflineMeasurements) => {
      setOfflineMeasurements(offlineMeasurements);
    },
    [],
  );

  const onActivation = useCallback((activationId: string) => {
    // the device has been activated with activationId
  }, []);

  const onImageData = useCallback((imageValidity: ImageValidity) => {
    let message: string;
    let des:string;
    if (imageValidity != ImageValidity.VALID) {
      switch (imageValidity) {
        case ImageValidity.INVALID_DEVICE_ORIENTATION:
          // message = 'Unsupported Orientation';
          message = 'ERROR_10001';
          des='ERROR_10006';
          break;
        case ImageValidity.TILTED_HEAD:
          // message = 'Head Tilted';
          message = 'ERROR_10003';
          des='ERROR_10008';
          break;
        case ImageValidity.FACE_TOO_FAR: // Placeholder, currently not supported
          // message = 'You Are Too Far';
          message = 'ERROR_10004';
          des='ERROR_10009';
          break;
        case ImageValidity.UNEVEN_LIGHT:
          // message = 'Uneven Lighting';
          message = 'ERROR_10005';
          des='ERROR_10010';
          break;
        case ImageValidity.INVALID_ROI:
        default:
          // message = 'Face Not Detected';
          message = 'ERROR_10002';
          des='ERROR_10007';
      }
      setInfo({
        type: InfoType.INSTRUCTION,
        message: message,
        des:des,
      });
    } else {
      setInfoWithDismiss({ type: InfoType.NONE });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await monitor.initialize({
          licenseKey,
          licenseInfo: {
            onEnabledVitalSigns,
            onOfflineMeasurement,
            onActivation,
          },
        });
        console.log(`Initialized monitor`);
        setIsMonitorReady(true);
        setError({ code: -1 });
      } catch (e) {
        console.error('Error initializing HealthMonitor', e);
        setIsMonitorReady(false);
        setError({ code: e.errorCode });
      }
    })();
  }, [licenseKey, productId]);
  const mobile = useMemo(() => isMobile(), []);
  const desktop = useMemo(() => !isTablet() && !isMobile(), []);
  useEffect(() => {
    (async () => {
      try {
        if (!isMonitorReady || !processingTime || !video.current) {
          return;
        }
      //   const subjectDemographic:SubjectDemographic ={
      //     sex: Sex.MALE,
      //     age: 36,
      //     weight: 82,
      //     height: 180,
      // };
        sessionState === SessionState.ACTIVE && session.terminate();
        const options: FaceSessionOptions = {
          input: video.current,
          cameraDeviceId: cameraId,
          processingTime,
          onVitalSign,
          onFinalResults,
          onError,
          onWarning,
          onStateChange,
          
          //orientation: DeviceOrientation.PORTRAIT,
          orientation: (desktop)?DeviceOrientation.LANDSCAPE_RIGHT:DeviceOrientation.PORTRAIT,
          //userInformation: {sex: Sex.MALE, age: 35, weight: 75, height: 165, smokingStatus: SmokingStatus.NON_SMOKER},
          strictMeasurementGuidance:true,
          onImageData,
          // isAnalyticsEnabled: true,
          /*******************************************************************************
           * For accurate vital signs calculation the user's parameters should be provided.
           * The following is an example of how the parameters are passed to the SDK:
           *
           * subjectDemographic: {sex: Sex.MALE, age: 35, weight: 75, height: 165}
           *
           * When measuring a new user then a new session must be created using the
           * new user's parameters
           * The parameters are used locally on the device only for the vital sign
           * calculation, and deleted when the session is terminated.
           *******************************************************************************/
        };
        const faceSession = await monitor.createFaceSession(options);
        
        console.log(`Session created`);
        setSession(faceSession);
        setError({ code: -1 });
      } catch (e) {
        await monitor.reset();
        setError({ code: e.errorCode });
        console.error('Error creating a session', e);
      }
    })();
  }, [processingTime, isMonitorReady]);

  useEffect(() => {
    if (startMeasuring) {
      if (sessionState === SessionState.ACTIVE) {
        session.start();
        setError({ code: -1 });
      }
    } else {
      sessionState === SessionState.MEASURING && session.stop();
    }
  }, [startMeasuring]);

  return {
    sessionState,
    vitalSigns: {
      pulseRate: {
        value: vitalSigns?.pulseRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledPulseRate,
        confidenceLevel:ConfidenceLevel[vitalSigns?.pulseRate?.confidenceLevel],
      },
      oxygenSaturation: {
        value: vitalSigns?.oxygenSaturation?.value,
        isEnabled: enabledVitalSigns?.isEnabledSpo2,
      },
      respirationRate: {
        value: vitalSigns?.respirationRate?.value,
        isEnabled: enabledVitalSigns?.isEnabledRespirationRate,
        confidenceLevel:ConfidenceLevel[vitalSigns?.respirationRate?.confidenceLevel],
      },
      sdnn: {
        value: vitalSigns?.sdnn?.value,
        isEnabled: enabledVitalSigns?.isEnabledSdnn,
        confidenceLevel:ConfidenceLevel[vitalSigns?.sdnn?.confidenceLevel],
      },
      stress: {
        value: StressLevel[vitalSigns?.stressLevel?.value],
        isEnabled: enabledVitalSigns?.isEnabledStressLevel,
      },
      stressIndex: {
        value: vitalSigns?.stressIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledStressIndex,
      },
      spo2: {
        value: null, //TODO Spo2 is currently disabled by algo
        isEnabled: false, //enabledVitalSigns?.isEnabledSpo2,
      },
      bloodPressure: {
        value: vitalSigns?.bloodPressure?.value,
        isEnabled: enabledVitalSigns?.isEnabledBloodPressure,
      },
      rri: {
        value: vitalSigns?.rri?.value,
        isEnabled: enabledVitalSigns?.isEnabledRri,
        confidenceLevel:ConfidenceLevel[vitalSigns?.rri?.confidenceLevel],
      },
      meanRri: {
        value: vitalSigns?.meanRri?.value,
        isEnabled: enabledVitalSigns?.isEnabledMeanRri,
        confidenceLevel:ConfidenceLevel[vitalSigns?.meanRri?.confidenceLevel],
      },
      rmssd: {
        value: vitalSigns?.rmssd?.value,
        isEnabled: enabledVitalSigns?.isEnabledRmssd,
      },
      sd1: {
        value: vitalSigns?.sd1?.value,
        isEnabled: enabledVitalSigns?.isEnabledSd1,
      },
      sd2: {
        value: vitalSigns?.sd2?.value,
        isEnabled: enabledVitalSigns?.isEnabledSd2,
      },
      prq: {
        value: vitalSigns?.prq?.value,
        isEnabled: enabledVitalSigns?.isEnabledPrq,
        confidenceLevel:ConfidenceLevel[vitalSigns?.prq?.confidenceLevel],
      },
      pnsIndex: {
        value: vitalSigns?.pnsIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledPnsIndex,
      },
      pnsZone: {
        value: PnsZone[vitalSigns?.pnsZone?.value],
        isEnabled: enabledVitalSigns?.isEnabledPnsZone,
      },
      snsIndex: {
        value: vitalSigns?.snsIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledSnsIndex,
      },
      snsZone: {
        value: SnsZone[vitalSigns?.snsZone?.value],
        isEnabled: enabledVitalSigns?.isEnabledSnsZone,
      },
      wellnessLevel: {
        value: WellnessLevel[vitalSigns?.wellnessLevel?.value],
        isEnabled: enabledVitalSigns?.isEnabledWellnessLevel,
      },
      wellnessIndex: {
        value: vitalSigns?.wellnessIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledWellnessIndex,
      },
      lfhf: {
        value: vitalSigns?.lfhf?.value,
        isEnabled: enabledVitalSigns?.isEnabledLfhf,
      },
      hemoglobin: {
        value: vitalSigns?.hemoglobin?.value,
        isEnabled: enabledVitalSigns?.isEnabledHemoglobin,
      },
      hemoglobinA1c: {
        value: vitalSigns?.hemoglobinA1c?.value,
        isEnabled: enabledVitalSigns?.isEnabledHemoglobinA1c,
      },
      highHemoglobinA1CRisk: {
        value: HighHemoglobinA1CRisk[vitalSigns?.highHemoglobinA1CRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledHighHemoglobinA1CRisk,
      },
      highBloodPressureRisk: {
        value: HighBloodPressureRisk[vitalSigns?.highBloodPressureRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledHighBloodPressureRisk,
      },
      ascvdRisk : {
        value: vitalSigns?.ascvdRisk?.value,
        isEnabled: enabledVitalSigns?.isEnabledAscvdRisk,
      },
      heartAge: {
        value: vitalSigns?.heartAge?.value,
        isEnabled: enabledVitalSigns?.isEnabledHeartAge,
      },
      normalizedStressIndex: {
        value: vitalSigns?.normalizedStressIndex?.value,
        isEnabled: enabledVitalSigns?.isEnabledNormalizedStressIndex,
      },
      highFastingGlucoseRisk: {
        value: HighFastingGlucoseRisk[vitalSigns?.highFastingGlucoseRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledHighFastingGlucoseRisk,
      },
      highTotalCholesterolRisk: {
        value: HighTotalCholesterolRisk[vitalSigns?.highTotalCholesterolRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledHighTotalCholesterolRisk,
      },
      lowHemoglobinRisk:{
        value: LowHemoglobinRisk[vitalSigns?.lowHemoglobinRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledLowHemoglobinRisk,
      },
      ascvdRiskLevel:{
        value: AscvdRiskLevel[vitalSigns?.ascvdRisk?.value],
        isEnabled: enabledVitalSigns?.isEnabledAscvdRiskLevel,
      },
      cardiacWorkload:{
        value: vitalSigns?.cardiacWorkload?.value,
        isEnabled: enabledVitalSigns?.isEnabledCardiacWorkload,
      },
      meanArterialPressure:{
        value: vitalSigns?.meanArterialPressure?.value,
        isEnabled: enabledVitalSigns?.isEnabledMeanArterialPressure,
      },
      pulsePressure:{
        value: vitalSigns?.pulsePressure?.value,
        isEnabled: enabledVitalSigns?.isEnabledPulsePressure,
      },
    },
    offlineMeasurements,
    error,
    warning,
    info,
  };
};

export default useMonitor;
